import React from "react";
import { Link } from "react-router-dom";
import { FaStar } from "react-icons/fa";
import { MapPin } from "lucide-react";
import Navbar from "./common/Navbar";

interface TourHeroProps {
  title: string;
  location: string;
  description: string;
  heroImage: string;
  rating?: number;
  reviewCount?: string;
}

const TourHero: React.FC<TourHeroProps> = ({
  title,
  location,
  description,
  heroImage,
  rating = 5,
  reviewCount = "100+ Reviews"
}) => {
  return (
    <div 
      className="relative h-[85vh] md:h-[75vh] mt-0 overflow-visible bg-cover bg-center flex items-center justify-center"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${heroImage})`
      }}
    >
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent pointer-events-none"></div>
      
      {/* Navbar - absolutely positioned at top */}
      <header className="absolute top-0 left-0 right-0 z-50">
        <Navbar />
      </header>
      
      {/* Hero content */}
      <div className="relative z-10 text-center text-white px-4 max-w-4xl pt-16 md:pt-20">
        <div className="animate-fade-up animation-delay-100">
          <div className="flex items-center gap-2 mb-4 md:mb-6 justify-center">
            <div className="flex items-center gap-1">
              {[...Array(rating)].map((_, i) => (
                <FaStar key={i} className="text-amber-400" />
              ))}
            </div>
            <span className="text-white/90 text-xs md:text-sm">{reviewCount}</span>
          </div>

          <div className="flex items-center gap-2 mb-3 md:mb-4 justify-center">
            <div className="bg-amber-700/20 p-1.5 md:p-2 rounded-full">
              <MapPin className="text-amber-400 h-4 w-4 md:h-5 md:w-5" />
            </div>
            <span className="text-white font-medium text-base md:text-lg">{location}</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-4 md:mb-6 text-white leading-tight">
            {title}
          </h1>
          
          <p className="text-white/90 text-base md:text-xl mb-6 md:mb-8 max-w-3xl mx-auto">
            {description}
          </p>
          
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center">
            <Link 
              to="/contact" 
              className="px-6 md:px-8 py-3 md:py-4 text-base font-extrabold leading-none text-center text-white bg-[rgba(192,91,42,1)] rounded-[40px] shadow-[0px_8px_20px_rgba(192,91,42,0.8)] hover:bg-[rgba(192,91,42,0.9)] transition-all"
            >
              <span>Contact Us</span>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Bottom gradient */}
      <div className="absolute bottom-0 w-full h-16 bg-gradient-to-t from-gray-50 to-transparent"></div>
    </div>
  );
};

export default TourHero; 